import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Globe, MessageCircle } from "lucide-react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, ROLE_RANK, type ServiceRequest, type RequestStatus } from "@clevis/shared-types";
import { Button, Card, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { RequestFormSetup } from "../../components/RequestFormSetup";
import { AiChatSetup } from "../../components/AiChatSetup";
import { useAccess } from "../../hooks/useAccess";
import { useEntitlements } from "../../hooks/useEntitlements";

// The online-request inbox. One click to a pre-filled draft estimate, one
// click to a scheduled job with a tech on it. Dense rows, few statuses. Each
// request shows its type of service, department queue and lead source.

type Row = ServiceRequest & { id: string };
interface Member {
  uid: string;
  name: string;
}

const STATUS_LABEL: Record<RequestStatus, string> = {
  new: "New",
  quoted: "Quoted",
  scheduled: "Scheduled",
  closed: "Closed",
};
const STATUS_STYLE: Record<RequestStatus, string> = {
  new: "bg-brandorange/15 text-brandorange-dark",
  quoted: "bg-steel/15 text-ink",
  scheduled: "bg-success/15 text-success",
  closed: "bg-steel/10 text-steel line-through",
};

function age(ms: number): string {
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 60) return `${Math.max(mins, 0)}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / (60 * 24))}d ago`;
}

export function RequestsPage() {
  const { tenantId, role } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [allDepartments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const { hasFeature } = useEntitlements();
  const departments = hasFeature("departments") ? allDepartments : [];
  const [tenantInfo, setTenantInfo] = useState({ slug: "", name: "" });
  const [dept, setDept] = useState("all");
  const [setupOpen, setSetupOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(() => window.location.hash === "#chat");
  const can = useAccess();
  const [tab, setTab] = useState<"inbox" | "done">("inbox");
  const [scheduling, setScheduling] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; href?: string; hrefLabel?: string } | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(query(collection(db, paths.tenantRequests(tenantId)), orderBy("createdAt", "desc")), (snap) =>
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ServiceRequest) }))),
      ),
      onSnapshot(collection(db, paths.tenantMembers(tenantId)), (snap) =>
        setMembers(
          snap.docs
            .filter((d) => d.get("disabled") !== true)
            .map((d) => ({ uid: d.id, name: (d.get("displayName") as string) || (d.get("email") as string) })),
        ),
      ),
      onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
        setDepartments(snap.docs.filter((d) => d.get("kind") !== "group").map((d) => ({ id: d.id, name: (d.get("name") as string) ?? "Department" })).sort((a, b) => a.name.localeCompare(b.name))),
      ),
      onSnapshot(doc(db, paths.tenant(tenantId)), (snap) =>
        setTenantInfo({ slug: (snap.get("slug") as string) ?? "", name: (snap.get("name") as string) ?? "" }),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  const inDept = (r: Row) => dept === "all" || (dept === "none" ? !r.departmentId : r.departmentId === dept);
  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          inDept(r) &&
          (tab === "inbox" ? r.status === "new" || r.status === "quoted" : r.status === "scheduled" || r.status === "closed"),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, tab, dept],
  );
  const deptName = (id: string | null | undefined) => departments.find((d) => d.id === id)?.name ?? "";
  const inboxCount = rows.filter((r) => r.status === "new" || r.status === "quoted").length;

  async function toEstimate(r: Row) {
    if (!tenantId) return;
    setBusy(`e${r.id}`);
    setMessage(null);
    try {
      const res = await call<{ number?: number; alreadyCreated?: boolean }>("createEstimateFromRequest", {
        tenantId,
        requestId: r.id,
      });
      setMessage({
        text: res.alreadyCreated
          ? "This request already has an estimate."
          : `Draft estimate #${res.number} created for ${r.name} — add lines from the pricebook and send it.`,
        href: "/estimates",
        hrefLabel: "Open estimates →",
      });
    } catch (err) {
      setMessage({ text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function schedule(r: Row, techUid: string, date: string, start: string, end: string, departmentId: string) {
    if (!tenantId) return;
    setBusy(`s${r.id}`);
    setMessage(null);
    try {
      const startAt = new Date(`${date}T${start}`).getTime();
      const endAt = new Date(`${date}T${end}`).getTime();
      await call("scheduleJobFromRequest", {
        tenantId,
        requestId: r.id,
        techUid: techUid || null,
        departmentId: departmentId || null,
        startAt,
        endAt: endAt > startAt ? endAt : null,
      });
      const techName = members.find((m) => m.uid === techUid)?.name;
      setMessage({
        text: `Job created${techName ? ` and assigned to ${techName}` : ""}.`,
        href: "/schedule",
        hrefLabel: "Open schedule →",
      });
      setScheduling(null);
    } catch (err) {
      setMessage({ text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(r: Row) {
    if (!tenantId) return;
    setBusy(`d${r.id}`);
    setMessage(null);
    try {
      await call("closeRequest", { tenantId, requestId: r.id });
    } catch (err) {
      setMessage({ text: (err as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Requests</h1>
        <div className="flex flex-wrap items-center gap-2">
        {departments.length ? (
          <Select className="w-auto py-1 text-xs" value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="all">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name} ({rows.filter((r) => r.departmentId === d.id && (r.status === "new" || r.status === "quoted")).length})
              </option>
            ))}
            <option value="none">No department</option>
          </Select>
        ) : null}
        <Button variant={setupOpen ? "secondary" : "ghost"} className="border border-steel/30 px-3 py-1.5 text-sm" onClick={() => setSetupOpen((v) => !v)}>
          <Globe size={14} className="mr-1.5 inline" />
          {setupOpen ? "Close website form" : "Website form & embed code"}
        </Button>
        {can("requests.chat") ? (
          <Button variant={chatOpen ? "secondary" : "ghost"} className="border border-steel/30 px-3 py-1.5 text-sm" onClick={() => setChatOpen((v) => !v)}>
            <MessageCircle size={14} className="mr-1.5 inline" />
            {chatOpen ? "Close AI chat" : "AI chat scheduling"}
          </Button>
        ) : null}
        <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
          {(["inbox", "done"] as const).map((t) => (
            <button
              key={t}
              className={cn("rounded-md px-3 py-1 capitalize", tab === t ? "bg-ink text-white" : "text-steel")}
              onClick={() => setTab(t)}
            >
              {t === "inbox" ? `Inbox${inboxCount > 0 ? ` (${inboxCount})` : ""}` : "Done"}
            </button>
          ))}
        </div>
        </div>
      </div>

      {setupOpen && tenantId && tenantInfo.slug ? (
        <RequestFormSetup
          tenantId={tenantId}
          slug={tenantInfo.slug}
          company={tenantInfo.name}
          departments={departments}
          canEdit={!!role && ROLE_RANK[role] >= ROLE_RANK.admin}
        />
      ) : null}

      {chatOpen && tenantId && tenantInfo.slug && can("requests.chat") ? (
        <AiChatSetup tenantId={tenantId} slug={tenantInfo.slug} company={tenantInfo.name} canEdit={!!role && ROLE_RANK[role] >= ROLE_RANK.admin} />
      ) : null}

      <Card className="border-steel/25 bg-cream/60 p-3 text-xs text-steel">
        Customers request work at{" "}
        <SlugLink tenantId={tenantId} />
        {" "}— share that link on your website, Google profile or invoices, and new requests land here.
      </Card>

      {message ? (
        <p className="text-xs text-ink">
          {message.text}{" "}
          {message.href ? (
            <Link href={message.href} className="font-semibold text-brandorange-dark hover:text-brandorange">
              {message.hrefLabel}
            </Link>
          ) : null}
        </p>
      ) : null}

      <Card className="p-0">
        {visible.map((r) => (
          <div key={r.id} className="border-b border-steel/10 px-4 py-2 last:border-0">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-ink">{r.name}</span>
                  <span className="shrink-0 rounded-full bg-steel/10 px-2 py-0.5 text-[10px] text-steel">
                    {r.serviceType || (r.kind === "estimate" ? "Estimate" : "Service call")}
                  </span>
                  {deptName(r.departmentId) ? (
                    <span className="shrink-0 rounded-full bg-ink/10 px-2 py-0.5 text-[10px] text-ink">{deptName(r.departmentId)}</span>
                  ) : null}
                  {r.referral ? (
                    <span className="shrink-0 rounded-full bg-brandorange/15 px-2 py-0.5 text-[10px] text-brandorange-dark" title="Came through a customer's referral link (Clevis Relay). The job stays tied to the referral; the reward goes out when it's paid.">
                      Referred by {(r.referral.name || "a customer").split(" ")[0]}
                    </span>
                  ) : null}
                  <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[10px]", STATUS_STYLE[r.status])}>
                    {STATUS_LABEL[r.status]}
                  </span>
                </div>
                <div className="truncate text-xs text-ink/80">{r.details}</div>
                <div className="truncate text-xs text-steel">
                  {age(r.createdAt)}
                  {r.email ? ` · ${r.email}` : ""}
                  {r.phone ? ` · ${r.phone}` : ""}
                  {r.address ? ` · ${r.address}` : ""}
                  {r.preferredTimes ? ` · prefers ${r.preferredTimes}` : ""}
                </div>
                {leadLine(r) ? <div className="truncate text-[11px] text-steel">Lead: {leadLine(r)}</div> : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {r.status !== "closed" && !r.estimateId ? (
                  <Button
                    variant="secondary"
                    className="px-2.5 py-1 text-xs"
                    busy={busy === `e${r.id}`}
                    onClick={() => void toEstimate(r)}
                  >
                    → Draft estimate
                  </Button>
                ) : null}
                {r.status !== "closed" && !r.jobId ? (
                  <Button
                    className="px-2.5 py-1 text-xs"
                    onClick={() => setScheduling((v) => (v === r.id ? null : r.id))}
                  >
                    {scheduling === r.id ? "Cancel" : "Schedule job"}
                  </Button>
                ) : null}
                {r.status === "new" ? (
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    busy={busy === `d${r.id}`}
                    onClick={() => void dismiss(r)}
                  >
                    Dismiss
                  </Button>
                ) : null}
              </div>
            </div>
            {scheduling === r.id ? (
              <ScheduleInline
                members={members}
                departments={departments}
                defaultDepartmentId={r.departmentId ?? ""}
                busy={busy === `s${r.id}`}
                onSchedule={(techUid, date, start, end, departmentId) => void schedule(r, techUid, date, start, end, departmentId)}
              />
            ) : null}
          </div>
        ))}
        {visible.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">
            {tab === "inbox" ? "No open requests — new ones land here the moment they're submitted." : "Nothing here yet."}
          </div>
        ) : null}
      </Card>
    </div>
  );
}

/** Where a request came from: the channel, what they said, the campaign tags. */
function leadLine(r: Row): string {
  const l = r.lead ?? { howHeard: "", utmSource: "", utmMedium: "", utmCampaign: "", page: "" };
  const channel = r.referral
    ? `${r.referral.name || "a customer"}'s referral link`
    : r.source === "website"
      ? "your website"
      : r.source === "web"
        ? "Clevis request link"
        : r.source === "chat"
          ? "website chat"
          : r.source;
  const campaign = [l.utmSource, l.utmMedium, l.utmCampaign].filter(Boolean).join(" / ");
  return [channel, l.howHeard ? `"${l.howHeard}"` : "", campaign ? `campaign ${campaign}` : "", l.page ? `page ${l.page}` : ""]
    .filter(Boolean)
    .join(" · ");
}

// The tenant's shareable intake link, with one-click copy.
function SlugLink({ tenantId }: { tenantId: string | null }) {
  const [slug, setSlug] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => setSlug((snap.get("slug") as string) ?? ""));
  }, [tenantId]);
  if (!slug) return <span className="font-mono">…</span>;
  const url = `${window.location.origin}/request/${slug}`;
  return (
    <>
      <a href={url} target="_blank" rel="noreferrer" className="font-mono text-ink hover:text-brandorange">
        {url.replace(/^https?:\/\//, "")}
      </a>{" "}
      <button
        className="font-semibold text-brandorange-dark hover:text-brandorange"
        onClick={() => {
          void navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "copied ✓" : "copy"}
      </button>
    </>
  );
}

function ScheduleInline({
  members,
  departments,
  defaultDepartmentId,
  busy,
  onSchedule,
}: {
  members: Member[];
  departments: Array<{ id: string; name: string }>;
  defaultDepartmentId: string;
  busy: boolean;
  onSchedule: (techUid: string, date: string, start: string, end: string, departmentId: string) => void;
}) {
  const [techUid, setTechUid] = useState("");
  const [departmentId, setDepartmentId] = useState(defaultDepartmentId);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("10:00");
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg bg-cream/70 p-2">
      <div className="min-w-[160px] flex-1">
        <Label>Technician</Label>
        <Select value={techUid} onChange={(e) => setTechUid(e.target.value)}>
          <option value="">— unassigned —</option>
          {members.map((m) => (
            <option key={m.uid} value={m.uid}>
              {m.name}
            </option>
          ))}
        </Select>
      </div>
      {departments.length ? (
        <div>
          <Label>Department</Label>
          <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
            <option value="">None</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      <div>
        <Label>Date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <Label>Start</Label>
        <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
      </div>
      <div>
        <Label>End</Label>
        <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
      </div>
      <Button className="px-3 py-2 text-xs" busy={busy} disabled={!date} onClick={() => onSchedule(techUid, date, start, end, departmentId)}>
        Create & assign
      </Button>
    </div>
  );
}
